import React from "react";
import {
    BrowserRouter as Router,
    Routes,
    Route,
    Navigate,
} from "react-router-dom";
import { Provider } from "react-redux";
import { store } from "./store";
import { useAuth } from "./hooks/useAuth";
import Cookies from "js-cookie";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

// Компоненты страниц
import HomePage from "./components/HomePage";
import SignInPage from "./components/SignInPage";
// import SignUpPage from "./components/SignUpPage";
import DashboardLayout from "./components/layout/DashboardLayout";
import ProfilePage from "./pages/ProfilePage";

import UsersPage from "./pages/UsersPage";
import UserTreePage from "./pages/UserTreePage";
import RolesPage from "./pages/RolesPage";
import PatronsPage from "./pages/PatronsPage";
import ApiKeysPage from "./pages/ApiKeysPage";
import ApiStatisticsPage from "./pages/ApiStatisticsPage";
import ApiStatsPage from "./pages/ApiStats";
import SettingsPage from "./pages/SettingsPage";
import PaymentsPage from "./pages/PaymentsPage";
import { PaymentIncomePage } from "./pages/PaymentIncomePage";
import AdminTopUpPage from "./pages/AdminTopUpPage";
import AdminRussiaHandsPage from "./pages/AdminRussiaHandsPage";
import AuthInitializer from "./components/AuthInitializer";
import RoleProtectedRoute from "./components/RoleProtectedRoute";
import ProgramsPage from "./pages/ProgramsPage";
import CryptoWalletsPage from "./pages/CryptoWalletsPage";
import WebhooksPage from "./pages/WebhooksPage";
import WebhookEndpointsPage from "./pages/WebhookEndpointsPage";
import GameLoggerPage from "./pages/GameLoggerPage";
import TelegramLogsPage from "./pages/TelegramLogsPage";
import AdminNewsPage from "./pages/AdminNewsPage";
import AdminNewsEditPage from "./pages/AdminNewsEditPage";
import AdminNewsCreatePage from "./pages/AdminNewsCreatePage";
import HelpTreePage from "./pages/HelpTreePage";
import HelpNodeEditPage from "./pages/HelpNodeEditPage";
import MarathonStatisticsPage from "./pages/MarathonStatisticsPage";
import { AdminDashboard } from "./pages/AdminDashboard";
import { AdminWithdrawals } from "./pages/AdminWithdrawals";
import { AdminJivo } from "./pages/AdminJivo";
import { AdminMessaging } from "./pages/AdminMessaging";
import AdminMillenniumBroadcastPage from "./pages/AdminMillenniumBroadcastPage";
import AdminDialogHost from "./components/ui/AdminDialogHost";
import { AdminGoldControl } from "./pages/AdminGoldControl";
import { AdminBingx } from "./pages/AdminBingx";
import PaymentStatsPage from "./pages/PaymentStatsPage";
import SafeStatisticsPage from "./pages/SafeStatisticsPage";
import StarControlPage from "./pages/StarControlPage";
import CharityControlPage from "./pages/CharityControlPage";
import SalaryPiggyBankPage from "./pages/SalaryPiggyBankPage";
import AdvertisingFundPage from "./pages/AdvertisingFundPage";
import IncentiveFundPage from "./pages/IncentiveFundPage";
import GameBonusPage from "./pages/GameBonusPage";
import TeamMembersPage from "./pages/TeamMembersPage";
import ProjectsList from "./pages/projects/ProjectsList";
import ProjectDetails from "./pages/projects/ProjectDetails";
import ProjectGallery from "./pages/projects/ProjectGallery";
import ProjectSubscribers from "./pages/projects/ProjectSubscribers";
import AutoReferralProgramsList from "./pages/autoReferral/AutoReferralProgramsList";
import AutoReferralProgramDetails from "./pages/autoReferral/AutoReferralProgramDetails";
import AutoReferralGallery from "./pages/autoReferral/AutoReferralGallery";
import AutoReferralSubscribers from "./pages/autoReferral/AutoReferralSubscribers";
import MarathonsPage from "./pages/MarathonsPage";
import MarathonDetailsPage from "./pages/MarathonDetailsPage";
import TokenOperationsPage from "./pages/TokenOperationsPage";
import AdminGarantsPage from "./pages/AdminGarantsPage";
import ProgramComparePage from "./pages/ProgramComparePage";
import { PurchasedSharesPage } from "./pages/PurchasedSharesPage";
import { GiftedSharesPage } from "./pages/GiftedSharesPage";
import { CreditSharesPage } from "./pages/CreditSharesPage";
import { InvestmentsPage } from "./pages/InvestmentsPage";
import { StakingPage } from "./pages/StakingPage";
import { VouchersPage } from "./pages/VouchersPage";
import InvestmentStrategiesPage from "./pages/InvestmentStrategiesPage";
// import AdamProxyStatsPage from "./pages/AdamProxyStatsPage"; // приховано разом з роутом
import StripeStatsPage from "./pages/StripeStatsPage";
import TributeStatsPage from "./pages/TributeStatsPage";
import AdminAltynPage from "./pages/AdminAltynPage";
import AdminFriendlyPayPage from "./pages/AdminFriendlyPayPage";
import { AdminTicketsPage } from "./pages/AdminTicketsPage";
import LandingAnalyticsPage from "./pages/LandingAnalyticsPage";
import WithdrawalReceiptSettingsPage from "./pages/WithdrawalReceiptSettingsPage";
import BloggersPage from "./pages/BloggersPage";
import MusicBotPage from "./pages/MusicBotPage";

// Компонент для защищенных маршрутов (только авторизация)
const AuthProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const { isAuthenticated } = useAuth();
    const token = Cookies.get("token");

    // Если есть токен в куки, но Redux store еще не синхронизирован, показываем загрузку
    if (token && !isAuthenticated) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">
                        Проверка авторизации...
                    </p>
                </div>
            </div>
        );
    }

    return isAuthenticated ? (
        <>{children}</>
    ) : (
        <Navigate to="/signin" replace />
    );
};

function App() {
    const queryClient = new QueryClient({
        defaultOptions: {
            queries: {
                retry: 1,
                refetchOnWindowFocus: false,
            },
        },
    });

    return (
        <QueryClientProvider client={queryClient}>
            <Provider store={store}>
                <Router>
                    <AuthInitializer>
                        <div className="App">
                            <Routes>
                                {/* Публичные маршруты */}
                                <Route path="/" element={<HomePage />} />
                                <Route
                                    path="/signin"
                                    element={<SignInPage />}
                                />
                                {/* <Route
                                    path="/signup"
                                    element={<SignUpPage />}
                                /> */}

                                {/* Защищенные маршруты */}
                                <Route
                                    path="/dashboard"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin", "editor"]}
                                            >
                                                <DashboardLayout>
                                                    <AdminDashboard />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                {/* Дополнительные маршруты dashboard */}
                                <Route
                                    path="/dashboard/profile"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin", "editor"]}
                                            >
                                                <DashboardLayout>
                                                    <ProfilePage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/users"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <UsersPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/patrons"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <PatronsPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/bloggers"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <BloggersPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/music"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <MusicBotPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/settings"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <SettingsPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/tree"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <UserTreePage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/roles"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <RolesPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/apikeys"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <ApiKeysPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/api-statistics"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <ApiStatisticsPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/api-stats"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <ApiStatsPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/payment-income"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <PaymentIncomePage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/payments"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <PaymentsPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/programs"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <ProgramsPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/dashboard/programs/payment-compare"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <ProgramComparePage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/projects"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <ProjectsList />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/dashboard/projects/:projectId"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <ProjectDetails />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/dashboard/projects/:projectId/gallery"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <ProjectGallery />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/dashboard/projects/:projectId/subscribers"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <ProjectSubscribers />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/auto-referral-programs"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <AutoReferralProgramsList />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/dashboard/auto-referral-programs/:programId"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <AutoReferralProgramDetails />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/dashboard/auto-referral-programs/:programId/gallery"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <AutoReferralGallery />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/dashboard/auto-referral-programs/:programId/subscribers"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <AutoReferralSubscribers />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/crypto-wallets"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <CryptoWalletsPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/payment-stats"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <PaymentStatsPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                {/* Adam Stripe Proxy — приховано (не ALNY Stripe)
                                <Route
                                    path="/dashboard/adam-proxy-stats"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <AdamProxyStatsPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />
                                */}

                                <Route
                                    path="/dashboard/stripe-stats"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <StripeStatsPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/tribute-stats"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <TributeStatsPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/webhooks"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <WebhooksPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/dashboard/webhook-endpoints"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <WebhookEndpointsPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/dashboard/telegram-logs"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <TelegramLogsPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/dashboard/game-logger"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <GameLoggerPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/news"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin", "editor"]}
                                            >
                                                <DashboardLayout>
                                                    <AdminNewsPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/dashboard/news/create"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin", "editor"]}
                                            >
                                                <DashboardLayout>
                                                    <AdminNewsCreatePage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                {/* Help (Knowledge Base) */}
                                <Route
                                    path="/dashboard/help"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <HelpTreePage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/dashboard/help/new"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <HelpNodeEditPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/dashboard/help/:id"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <HelpNodeEditPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/dashboard/news/:id/edit"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin", "editor"]}
                                            >
                                                <DashboardLayout>
                                                    <AdminNewsEditPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/admin-topup"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <AdminTopUpPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/russia-hands"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <AdminRussiaHandsPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                {/* Удален маршрут handler-payments */}

                                <Route
                                    path="/dashboard/marathons"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <MarathonsPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/dashboard/marathons/:id"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <MarathonDetailsPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/marathon-statistics"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <MarathonStatisticsPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/token-operations"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <TokenOperationsPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/safe-statistics"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <SafeStatisticsPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/salary-piggy-bank"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <SalaryPiggyBankPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/incentive-fund"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <IncentiveFundPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/game-bonus"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <GameBonusPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/landing-analytics"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <LandingAnalyticsPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                {/* Новые админские маршруты */}
                                <Route
                                    path="/dashboard/admin/withdrawals"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <AdminWithdrawals />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/dashboard/admin/garants"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <AdminGarantsPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/vouchers"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <VouchersPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/purchased-shares"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <PurchasedSharesPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/gifted-shares"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <GiftedSharesPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/credit-shares"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <CreditSharesPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/admin/jivo"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <AdminJivo />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/admin/messaging"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <AdminMessaging />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/admin/millennium-broadcast"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <AdminMillenniumBroadcastPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/admin/withdrawal-receipts"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <WithdrawalReceiptSettingsPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/admin/gold-control"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <AdminGoldControl />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/admin/bingx"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <AdminBingx />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/dashboard/admin/altyn"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <AdminAltynPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/dashboard/admin/friendlypay"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <AdminFriendlyPayPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/admin/tickets"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <AdminTicketsPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/star-control"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <StarControlPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/charity-control"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <CharityControlPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/team"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <TeamMembersPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/investments"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <InvestmentsPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/investment-strategies"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <InvestmentStrategiesPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/dashboard/staking"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <StakingPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/dashboard/advertising-fund"
                                    element={
                                        <AuthProtectedRoute>
                                            <RoleProtectedRoute
                                                allowedRoles={["admin"]}
                                            >
                                                <DashboardLayout>
                                                    <AdvertisingFundPage />
                                                </DashboardLayout>
                                            </RoleProtectedRoute>
                                        </AuthProtectedRoute>
                                    }
                                />
                            </Routes>
                            <AdminDialogHost />
                        </div>
                    </AuthInitializer>
                </Router>
            </Provider>
        </QueryClientProvider>
    );
}

export default App;
